import omit from 'lodash/omit'
import { createContext, Dispatch, useEffect, useReducer, useState } from 'react'
import {
  Data,
  Page,
  Recipient,
  ServerErrors,
  State,
  Values,
} from 'src/subscribe'
import IsThisAGiftForSomeoneElse from './IsThisAGiftForSomeoneElse'
import PurchaserInformation from './PurchaserInformation'
import RecipientsInformation from './RecipientsInformation'
import Review from './Review'
import SubscriptionRates from './SubscriptionRates'

type AddRecipientClicked = {
  type: 'add recipient clicked'
}

type AllRecipientsRemoved = {
  type: 'all recipients removed'
}

type EditRecipientClicked = {
  payload: {
    recipient: Recipient
  }
  type: 'edit recipient clicked'
}

type IsThisAGiftForSomeoneElseBackClicked = {
  payload: {
    values: Values
  }
  type: 'is this a gift for someone else back clicked'
}

type IsThisAGiftForSomeoneElseContinueClicked = {
  payload: {
    values: Values
  }
  type: 'is this a gift for someone else continue clicked'
}

type PurchaserInformationBackClicked = {
  payload: {
    values: Values
  }
  type: 'purchaser information back clicked'
}

type PurchaserInformationContinueClicked = {
  payload: {
    values: Values
  }
  type: 'purchaser information continue clicked'
}

type RateSelected = {
  payload: {
    values: Values
  }
  type: 'rate selected'
}

type RecipientsInformationBackClicked = {
  payload: {
    recipientBeingEdited: Recipient
  }
  type: 'recipients information back clicked'
}

type RecipientsInformationCancelClicked = {
  type: 'recipients information cancel clicked'
}

type RecipientsInformationPaymentClicked = {
  payload: {
    recipientBeingEdited: Recipient
  }
  type: 'recipients information payment clicked'
}

type RemoveRecipientConfirmed = {
  payload: {
    recipient: Recipient
  }
  type: 'remove recipient confirmed'
}

type ReviewClicked = {
  payload: {
    recipientBeingEdited: Recipient
  }
  type: 'review clicked'
}

type ReviewPaymentClicked = {
  type: 'review payment clicked'
}

type SaveCompleted = {
  type: 'save completed'
}

type SaveErrored = {
  type: 'save errored'
}

type StartOverConfirmed = {
  type: 'start over confirmed'
}

type SubmitCompleted = {
  type: 'submit completed'
}

type SubmitErrored = {
  payload: {
    page?: Page
    serverErrors?: ServerErrors
  }
  type: 'submit errored'
}

type Action =
  | AddRecipientClicked
  | AllRecipientsRemoved
  | EditRecipientClicked
  | IsThisAGiftForSomeoneElseBackClicked
  | IsThisAGiftForSomeoneElseContinueClicked
  | PurchaserInformationBackClicked
  | PurchaserInformationContinueClicked
  | RateSelected
  | RecipientsInformationBackClicked
  | RecipientsInformationCancelClicked
  | RecipientsInformationPaymentClicked
  | RemoveRecipientConfirmed
  | ReviewClicked
  | ReviewPaymentClicked
  | SaveCompleted
  | SaveErrored
  | StartOverConfirmed
  | SubmitCompleted
  | SubmitErrored

type Reducer = (state: State, action: Action) => State

const reducer: Reducer = (state, action) => {
  let newValues = state.values
  if ('payload' in action && 'values' in action.payload) {
    newValues = {
      ...state.values,
      ...action.payload.values,
    }
  }

  switch (action.type) {
    case 'add recipient clicked':
      return {
        ...state,
        page: 'RecipientsInformation',
        serverErrors: {},
      }
    case 'all recipients removed':
      return {
        ...state,
        page: 'IsThisAGiftForSomeoneElse',
        serverErrors: {},
      }
    case 'edit recipient clicked':
      return {
        ...state,
        isSaving: true,
        page: 'RecipientsInformation',
        recipientBeingEdited: action.payload.recipient,
        serverErrors: {},
      }
    case 'is this a gift for someone else back clicked':
      return {
        ...state,
        isSaving: true,
        page: 'PurchaserInformation',
        values: newValues,
      }
    case 'is this a gift for someone else continue clicked':
      const shouldClearRecipients = state.values.is_gift !== newValues.is_gift
      if (shouldClearRecipients) {
        return {
          ...state,
          isSaving: true,
          page: 'RecipientsInformation',
          recipientBeingEdited: null,
          values: {
            ...newValues,
            recipients: {},
          },
        }
      }
      return {
        ...state,
        isSaving: true,
        page: 'RecipientsInformation',
        values: newValues,
      }
    case 'purchaser information back clicked':
      return {
        ...state,
        isSaving: true,
        page: 'SubscriptionRates',
        values: newValues,
      }
    case 'purchaser information continue clicked':
      if (
        state.serverErrors.subscribers_already_exist?.purchaser_email_address
      ) {
        return {
          ...state,
          isSaving: true,
          page: 'IsThisAGiftForSomeoneElse',
          // If 'purchaser information continue clicked' was dispatched, the
          // user entered another valid purchaser email address, so we can
          // clear the server error for now
          serverErrors: {},
          values: newValues,
        }
      }
      if (state.giftWasInHash && state.values.is_gift === '1') {
        // If 'gift' was in the hash (see subscribe.tsx) and is_gift is '1',
        // skip over IsThisAGiftForSomeoneElse
        return {
          ...state,
          // Only do this one time
          giftWasInHash: false,
          isSaving: true,
          page: 'RecipientsInformation',
          values: newValues,
        }
      }
      return {
        ...state,
        isSaving: true,
        page: 'IsThisAGiftForSomeoneElse',
        values: newValues,
      }
    case 'rate selected':
      return {
        ...state,
        isSaving: true,
        page: 'PurchaserInformation',
        values: newValues,
      }
    case 'recipients information back clicked':
      return {
        ...state,
        isSaving: true,
        page: 'IsThisAGiftForSomeoneElse',
        recipientBeingEdited: action.payload.recipientBeingEdited,
        serverErrors: {},
      }
    case 'recipients information cancel clicked':
      return {
        ...state,
        isSaving: true,
        page: 'Review',
        recipientBeingEdited: null,
      }
    case 'recipients information payment clicked':
      // This action will only get called for non-gifts
      return {
        ...state,
        isSaving: true,
        isSubmitting: true,
        // This is so the recipient will be loaded up if the user clicks back
        // from the Stripe checkout screen
        recipientBeingEdited: action.payload.recipientBeingEdited,
        values: {
          ...state.values,
          recipients: {
            // Deliberately not using '...state.values.recipients' here because
            // we want to clear any recipients that were originally gift
            // recipients
            [action.payload.recipientBeingEdited.id]:
              action.payload.recipientBeingEdited,
          },
        },
      }
    case 'remove recipient confirmed':
      return {
        ...state,
        isSaving: true,
        values: {
          ...state.values,
          recipients: omit(
            state.values.recipients,
            action.payload.recipient.id
          ),
        },
      }
    case 'review clicked':
      return {
        ...state,
        isSaving: true,
        page: 'Review',
        recipientBeingEdited: null,
        // If 'review clicked' was dispatched, the user entered a valid email
        // address, so any subscribers_already_exist errors can be removed
        // for now
        serverErrors: {},
        values: {
          ...state.values,
          recipients: {
            ...state.values.recipients,
            [action.payload.recipientBeingEdited.id]:
              action.payload.recipientBeingEdited,
          },
        },
      }
    case 'review payment clicked':
      return {
        ...state,
        isSaving: true,
        isSubmitting: true,
      }
    case 'save completed':
    case 'save errored':
      return {
        ...state,
        isSaving: false,
      }
    case 'start over confirmed':
      return {
        ...state,
        isSaving: true,
        page: 'SubscriptionRates',
        serverErrors: {},
        values: {},
      }
    case 'submit completed':
      return {
        ...state,
        isSubmitting: false,
      }
    case 'submit errored':
      if (
        action.payload.serverErrors?.renewal_exists?.purchaser_email_address
      ) {
        return {
          ...state,
          isSaving: true,
          isSubmitting: false,
          page: 'PurchaserInformation',
          serverErrors: action.payload.serverErrors,
        }
      }
      if (action.payload.serverErrors?.subscribers_already_exist) {
        if (
          action.payload.serverErrors.subscribers_already_exist
            .purchaser_email_address
        ) {
          return {
            ...state,
            isSaving: true,
            isSubmitting: false,
            page: 'PurchaserInformation',
            serverErrors: action.payload.serverErrors,
          }
        }
        if (!state.values.recipients) {
          throw new Error(
            'submit errored, have subscribers_already_exist but no state.values.recipients'
          )
        }
        const id = Object.keys(
          action.payload.serverErrors.subscribers_already_exist
        )[0]
        if (!id) {
          throw new Error(
            'submit errored, could not determine id from subscribers_already_exist'
          )
        }
        const recipientBeingEdited = state.values.recipients[id]
        if (!recipientBeingEdited) {
          throw new Error(
            `submit errored, could not find recipient with id ${id}`
          )
        }
        return {
          ...state,
          isSaving: true,
          isSubmitting: false,
          page: 'RecipientsInformation',
          recipientBeingEdited,
          serverErrors: action.payload.serverErrors,
        }
      }
      if (action.payload.serverErrors?.nonce) {
        if (
          action.payload.page !== 'RecipientsInformation' &&
          action.payload.page !== 'Review'
        ) {
          throw new Error(
            `submit errored with nonce error but unknown page ${action.payload.page}`
          )
        }
        return {
          ...state,
          isSaving: true,
          isSubmitting: false,
          page: action.payload.page,
          serverErrors: action.payload.serverErrors,
        }
      }
      if (action.payload.serverErrors?.startable_books) {
        if (
          action.payload.page !== 'RecipientsInformation' &&
          action.payload.page !== 'Review'
        ) {
          throw new Error(
            `submit errored with startable_books error but unknown page ${action.payload.page}`
          )
        }
        return {
          ...state,
          isSaving: true,
          isSubmitting: false,
          page: action.payload.page,
          serverErrors: action.payload.serverErrors,
        }
      }
      return {
        ...state,
        isSubmitting: false,
      }
  }
}

type ContextValue = {
  dispatch: Dispatch<Action>
  state: State
}

const initialState: State = {
  giftWasInHash: false,
  isSaving: false,
  isSubmitting: false,
  nonce: undefined,
  page: 'SubscriptionRates',
  recipientBeingEdited: null,
  serverErrors: {},
  values: {},
}

const defaultValue: ContextValue = {
  dispatch: (_) => {},
  state: initialState,
}

export const Context = createContext<ContextValue>(defaultValue)

function getUtmsFromLocalStorage(): Record<string, string> {
  const utmKeys = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_id',
    'utm_content',
    'utm_term',
    'utm_placement',
  ]
  const utms: Record<string, string> = {}
  utmKeys.forEach((key) => {
    const value = localStorage.getItem(`nureads:${key}`)
    if (value) {
      utms[key] = value
    }
  })
  return utms
}

const Subscribe = ({ data, debug }: { data: Data; debug: boolean }) => {
  const [submitInProgress, setSubmitInProgress] = useState<boolean>(false)

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    ...data.saved_state,
    nonce: data.nonce,
  })

  if (debug) {
    console.log('Subscribe, state = ', state)
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [state.page])

  useEffect(() => {
    const submit = async () => {
      const body = {
        nonce: state.nonce,
        page: state.page,
        state: {
          giftWasInHash: state.giftWasInHash,
          page: state.page,
          recipientBeingEdited: state.recipientBeingEdited,
          serverErrors: state.serverErrors,
          values: {
            ...state.values,
            ...getUtmsFromLocalStorage(),
          },
        },
        values: {
          ...state.values,
          ...getUtmsFromLocalStorage(),
        },
      }
      setSubmitInProgress(true)
      if (window.fbq) {
        window.fbq('track', 'InitiateCheckout')
        console.log('fbq track InitiateCheckout')
      }
      const response = await fetch('/subscribe/submit', {
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      })
      setSubmitInProgress(false)
      if (!response.ok) {
        try {
          const result = await response.json()
          dispatch({
            payload: {
              page: result.page,
              serverErrors: result.errors,
            },
            type: 'submit errored',
          })
        } catch {
          console.error('Error response and could not parse JSON')
          dispatch({
            payload: {},
            type: 'submit errored',
          })
        }
        return
      }
      try {
        const result = await response.json()
        dispatch({
          type: 'submit completed',
        })
        if (result.checkout_session_url) {
          window.location.href = result.checkout_session_url
        }
      } catch {
        console.error('200 response but could not parse JSON')
        dispatch({
          payload: {},
          type: 'submit errored',
        })
      }
    }
    if (state.isSubmitting && !submitInProgress) {
      submit()
    }
  }, [state])

  useEffect(() => {
    const save = async () => {
      const response = await fetch('/subscribe/save', {
        body: JSON.stringify({
          nonce: state.nonce,
          state: {
            giftWasInHash: state.giftWasInHash,
            page: state.page,
            recipientBeingEdited: state.recipientBeingEdited,
            serverErrors: state.serverErrors,
            values: {
              ...state.values,
              ...getUtmsFromLocalStorage(),
            },
          },
        }),
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'PUT',
      })
      if (!response.ok) {
        dispatch({
          type: 'save errored',
        })
        return
      }
      dispatch({
        type: 'save completed',
      })
    }
    if (state.isSaving) {
      save()
    }
  }, [state])

  return (
    <Context.Provider
      value={{
        dispatch,
        state,
      }}
    >
      <div className="nureads-subscribe">
        <div className="form">
          {state.page === 'SubscriptionRates' && (
            <SubscriptionRates
              rates={data.rates}
              rates_banner={data.rates_banner}
              rates_subtitle={data.rates_subtitle}
            />
          )}
          {state.page === 'PurchaserInformation' && (
            <PurchaserInformation banner={data.banner} />
          )}
          {state.page === 'IsThisAGiftForSomeoneElse' && (
            <IsThisAGiftForSomeoneElse />
          )}
          {state.page === 'RecipientsInformation' && (
            <RecipientsInformation
              autoChargeTerms={data.auto_charge_terms}
              internationalPricingNote={data.international_pricing_note}
              rates={data.rates}
              startableBooks={data.startable_books}
            />
          )}
          {state.page === 'Review' && (
            <Review startableBooks={data.startable_books} />
          )}
        </div>
      </div>
    </Context.Provider>
  )
}

export default Subscribe
